import {
  FormTreeViewSelect,
  FormCheckboxGroup,
  FormMultiSelector,
  TooltipTrigger,
  TooltipContent,
  FormMessage,
  FormSelect,
  Typography,
  Tooltip,
  Button,
  Form,
} from '@ui/components';
import { OnboardingSaveParkingDto, DayOfWeek } from '@interfaces/models';
import { RevertConfigurationDialog } from '@shared/components';
import { CityDto, ZoneDto } from '@interfaces/types';
import { useTranslation } from 'react-i18next';
import { Info } from '@ui/components/icons';
import { FC } from 'react';

import { useParkingConfigurationForm } from './use-parking-configuration-form';

interface ParkingConfigurationFormProps {
  defaultParkingConfiguration: OnboardingSaveParkingDto;
  days: DayOfWeek[];
  cities: CityDto[];
  zones: ZoneDto[];
  onSubmit: (parkingConfiguration: OnboardingSaveParkingDto) => void;
  onRevertChanges: () => void;
}

export const ParkingConfigurationForm: FC<ParkingConfigurationFormProps> = ({
  defaultParkingConfiguration,
  cities,
  zones,
  days,
  onSubmit,
  onRevertChanges,
}) => {
  const { t } = useTranslation();

  const {
    selectedCitiesCustomValue,
    selectedZonesCustomValue,
    selectedDaysCustomValue,
    unlimitedTimeSelected,
    selectedCitiesOptions,
    timeFromOptions,
    citiesOptions,
    timeToOptions,
    zonesOptions,
    daysOptions,
    sortCities,
    sortDays,
    form,
  } = useParkingConfigurationForm({
    defaultValues: defaultParkingConfiguration,
    cities,
    zones,
    days,
  });

  const handleSubmit = form.handleSubmit((data) => {
    onSubmit(data as OnboardingSaveParkingDto);
  });

  return (
    <div>
      <Form {...form}>
        <section className="space-y-6 mt-8">
          <div>
            <Typography variant="labelMediumMedium" className="text-weak mb-2">
              {t('COMPANY_POLICY.PARKING_CONFIGURATION_CITIES')}:
            </Typography>
            <FormMultiSelector
              componentProps={{
                contentSearch: {
                  placeholder: t('COMPANY_POLICY.PARKING_CONFIGURATION_SEARCH'),
                },
                label: citiesOptions.length
                  ? selectedCitiesCustomValue
                  : t('COMPANY_POLICY.PARKING_CONFIGURATION_SELECT_CITIES'),
                selectAllLabel: t('COMPANY_POLICY.PARKING_CONFIGURATION_SELECT_ALL_CITIES'),
              }}
              placeholder={t('COMPANY_POLICY.PARKING_CONFIGURATION_SELECT_CITIES')}
              disabled={!citiesOptions.length}
              formControl={form.control}
              sortFunction={sortCities}
              options={citiesOptions}
              name="cities"
            />
          </div>

          <div>
            <Typography variant="labelMediumMedium" className="text-weak mb-4">
              {t('COMPANY_POLICY.PARKING_CONFIGURATION_ZONES')}:
            </Typography>
            <div className="flex justify-between items-center gap-4">
              <Typography variant="labelMediumBook">{selectedZonesCustomValue}</Typography>
              <FormTreeViewSelect
                title={t('COMPANY_POLICY.PARKING_CONFIGURATION_SELECT_ZONES_TITLE')}
                nodeGroupLabel={t('COMPANY_POLICY.PARKING_CONFIGURATION_ZONES')}
                confirmButtonLabel={t('COMPANY_POLICY.ZONES_CONFIRM_SELECTION')}
                placeholder={t('COMPANY_POLICY.PARKING_CONFIGURATION_SEARCH')}
                cancelButtonLabel={t('COMPANY_POLICY.ZONES_REJECT_SELECTION')}
                nodeEditedLabel={t('COMPANY_POLICY.ZONES_CITY_EDITED')}
                nodes={selectedCitiesOptions}
                formControl={form.control}
                options={zonesOptions}
                name="zones"
              >
                <Button disabled={!zonesOptions.length} variant="link" size="small">
                  {t('COMPANY_POLICY.PARKING_CONFIGURATION_CHANGE_ZONES')}
                </Button>
              </FormTreeViewSelect>
            </div>
          </div>

          <div>
            <Typography variant="labelMediumMedium" className="text-weak mb-2">
              {t('COMPANY_POLICY.PARKING_CONFIGURATION_DAYS')}:
            </Typography>
            <FormMultiSelector
              componentProps={{
                label: selectedDaysCustomValue,
                selectAllLabel: t('COMPANY_POLICY.PARKING_CONFIGURATION_SELECT_ALL_DAYS'),
              }}
              placeholder={t('COMPANY_POLICY.PARKING_CONFIGURATION_SELECT_DAYS')}
              disabled={!daysOptions.length}
              formControl={form.control}
              sortFunction={sortDays}
              options={daysOptions}
              name="days"
            />
          </div>

          <div>
            <Typography variant="labelMediumMedium" className="text-weak mb-5">
              {t('COMPANY_POLICY.PARKING_CONFIGURATION_TIME')}:
            </Typography>

            <div className="flex items-center gap-4">
              <FormCheckboxGroup
                items={[
                  {
                    name: 'unlimitedTime',
                    label: t('COMPANY_POLICY.UNLIMITED_TIME'),
                  },
                ]}
                formControl={form.control}
                className="m-0"
              />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-weak" />
                </TooltipTrigger>
                <TooltipContent>
                  <Typography variant="labelSmallBook">
                    {t('COMPANY_POLICY.CONFIGURATION_TIME_TOOLTIP')}
                  </Typography>
                </TooltipContent>
              </Tooltip>
            </div>

            {!unlimitedTimeSelected && (
              <div>
                <div className="flex items-center gap-2 mt-6">
                  <FormSelect
                    placeholder={t('COMPANY_POLICY.PARKING_CONFIGURATION_FROM')}
                    formControl={form.control}
                    options={timeFromOptions}
                    hideErrorMessage
                    name="timeFrom"
                    size="medium"
                  />

                  <Typography className="text-weak lowercase">{t('COMPANY_POLICY.TO')}</Typography>

                  <FormSelect
                    placeholder={t('COMPANY_POLICY.PARKING_CONFIGURATION_TO')}
                    formControl={form.control}
                    options={timeToOptions}
                    hideErrorMessage
                    name="timeTo"
                    size="medium"
                  />
                </div>
                {(form.formState.errors.timeFrom || form.formState.errors.timeTo) && (
                  <FormMessage className="mt-2">
                    {form.formState.errors.timeFrom?.message ||
                      form.formState.errors.timeTo?.message}
                  </FormMessage>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mt-12">
            <Button onClick={handleSubmit} type="button">
              {t('COMPANY_POLICY.SAVE')}
            </Button>

            <RevertConfigurationDialog
              description={t('COMPANY_POLICY.PARKING_CONFIGURATION_REVERT_CHANGES_DESCRIPTION')}
              title={t('COMPANY_POLICY.PARKING_CONFIGURATION_REVERT_CHANGES_TITLE')}
              onRevertChanges={onRevertChanges}
            />
          </div>
        </section>
      </Form>
    </div>
  );
};
